import type { JointAngles } from './JointAngles';
import type { BodyNodeLink } from '../types';

export interface PoseRecord {
  t: number;
  poses: BodyNodeLink[];
  angles?: JointAngles;
  anglesPerPerson?: (JointAngles | undefined)[];
}

export interface PoseRecordStoreOptions {
  maxRecords?: number;
}

function trimOverflow<T>(items: T[], maxItems: number): void {
  const overflow = items.length - maxItems;
  if (overflow > 0) items.splice(0, overflow);
}

export class PoseRecordStore {
  private records: PoseRecord[] = [];
  private maxRecords: number;

  constructor(opts: PoseRecordStoreOptions = {}) {
    this.maxRecords = Math.max(1, opts.maxRecords ?? 600);
  }

  push(
    poses: BodyNodeLink[],
    angles: JointAngles | undefined,
    t: number,
    anglesPerPerson?: readonly (JointAngles | undefined)[],
  ): void {
    this.records.push({
      t,
      poses: poses.map((p) => ({
        nodes: p.nodes.slice(),
        links: p.links,
        fps: p.fps,
        inferenceTimeMs: p.inferenceTimeMs,
      })),
      angles: angles ? { ...angles } : undefined,
      anglesPerPerson: anglesPerPerson
        ? anglesPerPerson.map((a) => (a ? { ...a } : undefined))
        : undefined,
    });
    trimOverflow(this.records, this.maxRecords);
  }

  reset(): void {
    this.records = [];
  }

  setMaxRecords(n: number): void {
    this.maxRecords = Math.max(1, Math.floor(n));
    trimOverflow(this.records, this.maxRecords);
  }

  getAll(): readonly PoseRecord[] {
    return this.records;
  }

  count(): number {
    return this.records.length;
  }

  totalPoints(): number {
    let n = 0;
    for (const r of this.records) for (const p of r.poses) n += p.nodes.length;
    return n;
  }
}
